import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { ExportDocumentButton } from '../../export-document-button/export-document-button';
import { ExerciseCalendar } from '../shared/exercise-calendar/exercise-calendar';
import { CommissionReportsService } from '../../services/commission-reports.service';
import { SelectedDnService } from '../../services/selected-dn.service';
import { NavalMatrizItem } from '../../interfaces/commission-reports.interface';

interface SimulatorMetricRow {
  label: string;
  newVehicles: string;
  usedVehicles: string;
  total: string;
  inputType?: 'integer' | 'decimal' | 'percentage';
  totalEditable?: boolean;
}

interface NewPeriodRow {
  label: string;
  newVehicles: string;
  usedVehicles: string;
  total: string;
}

interface PenetrationRow {
  label: string;
  values: string[];
}

@Component({
  selector: 'comission-simulator',
  standalone: true,
  imports: [ExerciseCalendar, ExportDocumentButton],
  templateUrl: './comission-simulator.html',
  styleUrl: './comission-simulator.scss',
})
export class ComissionSimulator implements OnInit, OnDestroy {
  @ViewChild('simulatorContainer') simulatorContainer!: ElementRef<HTMLElement>;
  @ViewChild(ExerciseCalendar) periodCalendar?: ExerciseCalendar;

  readonly provisionalDaysPerMonth = 20;

  private updateFeedbackTimeout?: ReturnType<typeof setTimeout>;
  private dnSubscription?: Subscription;
  private matrixRequest?: Subscription;
  private hasExplicitPeriodSelection = false;
  private readonly editedClosingInputs = new Set<string>();


  isPenetrationVisible = false;
  isPenetrationLoading = false;
  hasPenetrationData = true;
  isUsingProvisionalMatrix = false;
  readonly regionalAverageDisplay = 'R$ 618.962';
  readonly nationalAverageDisplay = 'R$ 603.913';
  readonly regionalTrend: { value: string; direction: 'up' | 'down' } = {
    value: '82,8%',
    direction: 'up',
  };
  readonly nationalTrend: { value: string; direction: 'up' | 'down' } = {
    value: '87,4%',
    direction: 'up',
  };
  lastUpdatedInput = '';
  updateFeedbackMessage = '';
  selectedYear = 2026;
  selectedMonth = 5;
  isExporting = false;

  readonly penetrationColumns = [
    '0-39,9',
    '40-49,9',
    '50-54,9',
    '55-59,9',
    '60-64,9',
    '65-69,9',
    '70-74,9',
    '75-79,9',
    '80-84,9',
    '85-89,9',
    '90-99,9',
    '100-124,9',
    '125-149,9',
    '150-174,9',
    '175-199,9',
    '>=200',
  ];

  readonly penetrationRows: PenetrationRow[] = [
    { label: '≥ 55', values: Array(16).fill('0,00%') },
    { label: '50-54,9', values: Array(16).fill('0,00%') },
    { label: '45-49,9', values: Array(16).fill('0,00%') },
    { label: '40-44,9', values: Array(16).fill('0,00%') },
    { label: '≤ 40', values: Array(16).fill('0,00%') },
  ];

  /**
   * Matriz provisória (valores utilizados na versão anterior do simulador).
   * Aplicada como fallback quando a API naval-matriz não retorna dados para o DN/período,
   * para que o cálculo da comissão continue funcionando de forma provisória.
   */
  private readonly provisionalMatrixFallback: Record<string, string[]> = {
    '≥ 55': ['2,92%', '6,26%', '6,68%', '7,10%', '7,52%', '7,93%', '8,35%', '9,35%', '9,69%', '10,10%', '10,60%', '11,86%', '13,61%', '16,70%', '17,70%', '18,70%'],
    '50-54,9': ['2,21%', '4,73%', '5,04%', '5,36%', '5,67%', '5,99%', '6,30%', '7,06%', '7,31%', '7,62%', '8,00%', '8,95%', '10,27%', '12,60%', '13,36%', '14,11%'],
    '45-49,9': ['2,00%', '4,28%', '4,56%', '4,85%', '5,13%', '5,42%', '5,70%', '6,38%', '6,61%', '6,90%', '7,24%', '8,09%', '9,29%', '11,40%', '12,08%', '12,77%'],
    '40-44,9': ['1,52%', '3,26%', '3,48%', '3,70%', '3,92%', '4,13%', '4,35%', '4,87%', '5,05%', '5,26%', '5,52%', '6,18%', '7,09%', '8,70%', '9,22%', '9,74%'],
    '≤ 40': ['1,24%', '2,66%', '2,84%', '3,02%', '3,20%', '3,37%', '3,55%', '3,98%', '4,12%', '4,30%', '4,51%', '5,04%', '5,79%', '7,10%', '7,53%', '7,95%'],
  };

  readonly realizedRows: SimulatorMetricRow[] = [
    { label: 'Peso de Tabela Médio', newVehicles: '0,3274', usedVehicles: '1,0001', total: '1,3275' },
    { label: 'Contratos/dia útil', newVehicles: '8', usedVehicles: '8', total: '16' },
    { label: 'Contratos no período', newVehicles: '131', usedVehicles: '42', total: '173' },
    { label: 'RMKT', newVehicles: '0', usedVehicles: '0', total: '0' },
    { label: 'Vlr Financiado Médio', newVehicles: 'R$ 52.030', usedVehicles: 'R$ 16.681', total: 'R$ 68.711' },
    { label: 'Vol Agregados ponderado', newVehicles: '120', usedVehicles: '20', total: '140' },
    { label: 'ISR', newVehicles: '98%', usedVehicles: '68,21%', total: '166,21%' },
    { label: 'Penetration', newVehicles: '52,82%', usedVehicles: '0', total: '52,82%' },
    { label: 'Índice', newVehicles: '7,03%', usedVehicles: '2,26%', total: '9,29%' },
    { label: 'Comissão/Contrato', newVehicles: 'R$ 4.770', usedVehicles: 'R$ 14.877', total: 'R$ 19.647' },
    { label: 'Comissão/Mês', newVehicles: 'R$ 473.141', usedVehicles: 'R$ 151.694', total: 'R$ 624.835' },
  ];

  readonly closingPeriodRows: SimulatorMetricRow[] = [
    { label: 'Contratos/dia útil', newVehicles: '0', usedVehicles: '0', total: '', inputType: 'integer' },
    { label: 'RMKT', newVehicles: '', usedVehicles: '', total: '' },
    { label: 'Peso Novo', newVehicles: '0', usedVehicles: '0', total: '', inputType: 'decimal' },
    { label: 'ISR', newVehicles: '', usedVehicles: '', total: '0', inputType: 'percentage', totalEditable: true },
    { label: 'Penetration', newVehicles: '', usedVehicles: '', total: '0', inputType: 'percentage', totalEditable: true },
  ];

  readonly newPeriodRows: NewPeriodRow[] = [
    { label: 'Novo índice', newVehicles: '', usedVehicles: '', total: '—' },
    { label: 'Peso', newVehicles: '—', usedVehicles: '—', total: '' },
    { label: 'Comissão/Contrato', newVehicles: '—', usedVehicles: '—', total: '—' },
    { label: 'Comissão/Mês', newVehicles: '—', usedVehicles: '—', total: '—' },
    { label: 'Comissão RMKT', newVehicles: '', usedVehicles: '', total: '' },
  ];

  constructor(
    private commissionReportsService: CommissionReportsService,
    private selectedDnService: SelectedDnService
  ) {
    this.recalculateProvisionalResults();
  }

  ngOnInit(): void {
    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(dn => {
      if (Number(dn) > 0) {
        this.loadPenetrationMatrix();
      }
    });
  }

  ngOnDestroy(): void {
    this.dnSubscription?.unsubscribe();
    this.matrixRequest?.unsubscribe();
    clearTimeout(this.updateFeedbackTimeout);
  }

  togglePenetrationTable(): void {
    this.isPenetrationVisible = !this.isPenetrationVisible;

    if (this.isPenetrationVisible) {
      setTimeout(() => {
        document.getElementById('penetration-table')?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });
    }
  }

  onPeriodChange(period: { year: number; month: number }): void {
    this.selectedYear = period.year;
    this.selectedMonth = period.month;
    this.hasExplicitPeriodSelection = true;
    this.loadPenetrationMatrix();
  }

  private loadPenetrationMatrix(): void {
    const dn = this.selectedDnService.getSelectedDnAsNumber();
    if (!dn) {
      return;
    }

    const exercise = this.hasExplicitPeriodSelection
      ? Number(`${this.selectedYear}${String(this.selectedMonth).padStart(2, '0')}`)
      : undefined;
    this.matrixRequest?.unsubscribe();
    this.isUsingProvisionalMatrix = false;
    this.penetrationRows.forEach(row => row.values.fill('0,00%'));
    this.recalculateProvisionalResults();
    this.isPenetrationLoading = true;
    this.hasPenetrationData = true;
    this.matrixRequest = this.commissionReportsService.getNavalMatriz(exercise, dn).subscribe({
      next: items => {
        if (!this.hasExplicitPeriodSelection) {
          const latestExercise = this.getLatestExercise(items);
          if (latestExercise) {
            const year = Math.floor(latestExercise / 100);
            const month = latestExercise % 100;
            if (month >= 1 && month <= 12) {
              this.selectedYear = year;
              this.selectedMonth = month;
              this.periodCalendar?.syncPeriod(year, month);
            }
          }
        }
        const matchedRows = this.populatePenetrationMatrix(items);
        this.hasPenetrationData = items.length > 0 && matchedRows > 0;
        if (!this.hasPenetrationData) {
          this.applyProvisionalMatrix();
        }
        this.recalculateProvisionalResults();
        this.isPenetrationLoading = false;
      },
      error: error => {
        console.error('Erro ao carregar a matriz Penetration/ISR do simulador:', error);
        this.hasPenetrationData = false;
        this.isPenetrationLoading = false;
        this.applyProvisionalMatrix();
        this.recalculateProvisionalResults();
      }
    });
  }

  private getLatestExercise(items: NavalMatrizItem[]): number | undefined {
    return items.find(item =>
      Number.isInteger(item.exercicio) && String(item.exercicio).length === 6
    )?.exercicio;
  }

  private applyProvisionalMatrix(): void {
    this.penetrationRows.forEach(row => {
      const fallbackValues = this.provisionalMatrixFallback[row.label];
      row.values = fallbackValues
        ? [...fallbackValues]
        : Array(this.penetrationColumns.length).fill('0,00%');
    });
    this.isUsingProvisionalMatrix = true;
  }

  private populatePenetrationMatrix(items: NavalMatrizItem[]): number {
    const valueFields: { model: keyof NavalMatrizItem; api: string }[] = [
      { model: 'faixa_0_39_9', api: '0-39,9' },
      { model: 'faixa_40_49_9', api: '40-49,9' },
      { model: 'faixa_50_54_9', api: '50-54,9' },
      { model: 'faixa_55_59_9', api: '55-59,9' },
      { model: 'faixa_60_64_9', api: '60-64,9' },
      { model: 'faixa_65_69_9', api: '65-69,9' },
      { model: 'faixa_70_74_9', api: '70-74,9' },
      { model: 'faixa_75_79_9', api: '75-79,9' },
      { model: 'faixa_80_84_9', api: '80-84,9' },
      { model: 'faixa_85_89_9', api: '85-89,9' },
      { model: 'faixa_90_99_9', api: '90-99,9' },
      { model: 'faixa_100_124_9', api: '100-124,9' },
      { model: 'faixa_125_149_9', api: '125-149,9' },
      { model: 'faixa_150_174_9', api: '150-174,9' },
      { model: 'faixa_175_199_9', api: '175-199,9' },
      { model: 'faixa_Maior_200', api: '>200' },
    ];
    const normalizeRange = (range: string): string => {
      const normalized = range
        .trim()
        .toLocaleLowerCase()
        .replace(/[‐‑‒–—−]/g, '-')
        .replace(/≥/g, '>=')
        .replace(/≤/g, '<=')
        .replace(/\s/g, '')
        .replace(/%/g, '')
        .replace(/,/g, '.')
        .replace(/(\d+)\.99(?=$|-)/g, '$1.9');
      return normalized === '>55' ? '>=55' : normalized === '<40' ? '<=40' : normalized;
    };
    const getItemValue = (item: NavalMatrizItem, key: string): unknown =>
      (item as unknown as Record<string, unknown>)[key];
    const retailItems = items.filter(item =>
      String(getItemValue(item, 'tipo') ?? getItemValue(item, 'Tipo')).trim().toUpperCase() === 'VAREJO'
    );

    let matchedRows = 0;
    this.penetrationRows.forEach(row => {
      const apiRow = retailItems.find(item => {
        const apiRange = getItemValue(item, 'penetration') ?? getItemValue(item, 'Penetration');
        return apiRange != null && normalizeRange(String(apiRange)) === normalizeRange(row.label);
      });
      if (!apiRow) {
        return;
      }
      matchedRows += 1;

      row.values = valueFields.map(({ model, api }) => {
        const rawValue = getItemValue(apiRow, model) ?? getItemValue(apiRow, api);
        if (rawValue === null || rawValue === undefined || rawValue === '') {
          return '0,00%';
        }
        const normalizedValue = String(rawValue).trim().replace('%', '').replace(',', '.');
        const value = Number(normalizedValue);
        return Number.isFinite(value) ? `${value.toFixed(2).replace('.', ',')}%` : '0,00%';
      });
    });
    return matchedRows;
  }

  onExportRequested(): void {
    const element = this.simulatorContainer?.nativeElement;
    if (!element) {
      console.error('Não foi possível localizar o conteúdo do simulador para exportação.');
      return;
    }

    this.isExporting = true;
    html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      ignoreElements: (candidate) =>
        Boolean(candidate.closest?.('.period-selector, .type-picker-selector, .export-selector')),
      onclone: (_clonedDocument, clonedElement) => {
        this.copyExportInputValues(clonedElement as HTMLElement);
      },
    }).then((canvas) => {
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, canvas.width, canvas.height);

      const period = `${this.selectedYear}-${this.selectedMonth.toString().padStart(2, '0')}`;
      this.downloadFile(pdf.output('blob'), `simulador-comissionamento-${period}.pdf`);
    }).catch((error: unknown) => {
      console.error('Erro ao gerar PDF do Simulador de Comissionamento:', error);
    }).finally(() => {
      this.isExporting = false;
    });
  }

  private copyExportInputValues(clone: HTMLElement): void {
    const clonedInputs = clone.querySelectorAll<HTMLInputElement>('input.closing-input');

    clonedInputs.forEach(clonedInput => {
      const rowLabel = clonedInput.dataset['rowLabel'];
      const field = clonedInput.dataset['field'] as 'newVehicles' | 'usedVehicles' | 'total' | undefined;
      const row = this.closingPeriodRows.find(item => item.label === rowLabel);
      if (!row || !field) {
        return;
      }

      const text = clone.ownerDocument.createElement('span');
      const computedStyle = clone.ownerDocument.defaultView?.getComputedStyle(clonedInput);
      text.className = clonedInput.className;
      text.textContent = row[field];

      if (computedStyle) {
        for (let index = 0; index < computedStyle.length; index += 1) {
          const property = computedStyle.item(index);
          text.style.setProperty(
            property,
            computedStyle.getPropertyValue(property),
            computedStyle.getPropertyPriority(property)
          );
        }
      }

      for (const attribute of Array.from(clonedInput.attributes)) {
        if (attribute.name.startsWith('_ngcontent-')) {
          text.setAttribute(attribute.name, '');
        }
      }

      text.style.display = 'inline-flex';
      text.style.alignItems = 'center';
      text.style.justifyContent = 'center';
      clonedInput.parentElement?.replaceChild(text, clonedInput);
    });
  }

  onExcelExportRequested(period: {
    start: { year: number; month: number };
    end: { year: number; month: number };
  }): void {
    const pad = (value: number) => value.toString().padStart(2, '0');
    const periodLabel = `${period.start.year}${pad(period.start.month)}`;
    const rows: unknown[][] = [
      ['Realizado do dealer'],
      ['', 'Novos', 'Usados', 'Total'],
      ...this.realizedRows.map(row => [row.label, row.newVehicles, row.usedVehicles, row.total]),
      [],
      ['Simular o Fechamento'],
      ['', 'Novos', 'Usados', 'Total'],
      ...this.visibleClosingPeriodRows.map(row => [row.label, row.newVehicles, row.usedVehicles, row.total]),
      [],
      ['Nova comissão simulada'],
      ['', 'Novos', 'Usados', 'Total'],
      ...this.newPeriodRows.map(row => [row.label, row.newVehicles, row.usedVehicles, row.total]),
      [],
      ['Média Regional', this.regionalAverageDisplay],
      ['Média Nacional', this.nationalAverageDisplay],
      ['Nova Comissão Simulada', this.totalCommission],
      [],
      ['Penetration/ISR', ...this.penetrationColumns],
      ...this.penetrationRows.map(row => [row.label, ...row.values]),
    ];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Simulador');

    const fileName = `simulador-comissionamento-${periodLabel}.xlsx`;
    const workbookData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const fileBlob = new Blob([workbookData], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.downloadFile(fileBlob, fileName);
  }

  private downloadFile(fileBlob: Blob, fileName: string): void {
    const objectUrl = window.URL.createObjectURL(fileBlob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(objectUrl);
  }

  updateClosingInput(
    row: SimulatorMetricRow,
    vehicleType: 'newVehicles' | 'usedVehicles' | 'total',
    event: Event
  ): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    this.editedClosingInputs.add(`${row.label}:${vehicleType}`);

    if (row.inputType === 'integer') {
      row[vehicleType] = rawValue.replace(/\D/g, '');
    } else {
      const normalizedValue = rawValue.replace(/\./g, ',').replace(/[^\d,]/g, '');
      const [integerPart = '', ...decimalParts] = normalizedValue.split(',');
      const decimalPart = decimalParts.join('').slice(0, 2);
      row[vehicleType] = decimalParts.length > 0
        ? `${integerPart},${decimalPart}`
        : integerPart;
    }

    input.value = row[vehicleType];
    this.lastUpdatedInput = `${row.label}:${vehicleType}`;
    this.recalculateProvisionalResults();
    this.updateFeedbackMessage = `Campo ${row.label} alterado. Cálculos atualizados.`;
    clearTimeout(this.updateFeedbackTimeout);
    this.updateFeedbackTimeout = setTimeout(() => {
      this.lastUpdatedInput = '';
      this.updateFeedbackMessage = '';
    }, 1500);
  }

  isInputUpdated(
    row: SimulatorMetricRow,
    vehicleType: 'newVehicles' | 'usedVehicles' | 'total'
  ): boolean {
    return this.lastUpdatedInput === `${row.label}:${vehicleType}`;
  }

  get visibleClosingPeriodRows(): SimulatorMetricRow[] {
    return this.closingPeriodRows;
  }

  get availableExportMonths(): string[] {
    return [`${this.selectedYear}${this.selectedMonth.toString().padStart(2, '0')}`];
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    }).format(value);
  }

  get totalCommission(): string {
    return this.newPeriodRows.find(row => row.label === 'Comissão/Mês')?.total ?? this.formatCurrency(0);
  }

  get lastRealizedCommission(): string {
    return this.formatCurrency(this.getRealizedValue('Comissão/Mês', 'total'));
  }

  get commissionVariation(): { value: string; direction: 'up' | 'down' | 'stable' } | null {
    const realizedCommission = this.getRealizedValue('Comissão/Mês', 'total');
    if (realizedCommission === 0 || this.totalCommission === '—') {
      return null;
    }

    const simulatedCommission = this.getNewPeriodValue('Comissão/Mês', 'total');
    const variation = ((simulatedCommission - realizedCommission) / realizedCommission) * 100;
    const direction = variation > 0 ? 'up' : variation < 0 ? 'down' : 'stable';
    const percentage = variation === 0
      ? '0%'
      : `${variation > 0 ? '+' : ''}${new Intl.NumberFormat('pt-BR', {
        maximumFractionDigits: 1,
      }).format(variation)}%`;

    return { value: percentage, direction };
  }

  private recalculateProvisionalResults(): void {
    const commissionIndex = this.getMatrixCommissionIndex();
    this.setNewPeriodValue(
      'Novo índice',
      'total',
      commissionIndex === null ? '—' : `${commissionIndex.toFixed(2).replace('.', ',')}%`
    );

    const index = commissionIndex === null ? null : commissionIndex / 100;
    const newTableWeight = this.hasClosingInput('Peso Novo', 'newVehicles')
      ? this.getClosingValue('Peso Novo', 'newVehicles')
      : null;
    const usedTableWeight = this.hasClosingInput('Peso Novo', 'usedVehicles')
      ? this.getClosingValue('Peso Novo', 'usedVehicles')
      : null;
    const newFinancedAverage = this.getRealizedValue('Vlr Financiado Médio', 'newVehicles');
    const usedFinancedAverage = this.getRealizedValue('Vlr Financiado Médio', 'usedVehicles');
    const newCommissionPerContract = index !== null && newTableWeight !== null
      ? newFinancedAverage * index * newTableWeight
      : null;
    const usedCommissionPerContract = index !== null && usedTableWeight !== null
      ? usedFinancedAverage * index * usedTableWeight
      : null;
    const newContractsPerDay = this.hasClosingInput('Contratos/dia útil', 'newVehicles')
      ? this.getClosingValue('Contratos/dia útil', 'newVehicles')
      : null;
    const usedContractsPerDay = this.hasClosingInput('Contratos/dia útil', 'usedVehicles')
      ? this.getClosingValue('Contratos/dia útil', 'usedVehicles')
      : null;
    const newMonthlyCommission = newContractsPerDay !== null && newCommissionPerContract !== null
      ? newContractsPerDay * this.provisionalDaysPerMonth * newCommissionPerContract
      : null;
    const usedMonthlyCommission = usedContractsPerDay !== null && usedCommissionPerContract !== null
      ? usedContractsPerDay * this.provisionalDaysPerMonth * usedCommissionPerContract
      : null;

    this.setNewPeriodValue('Peso', 'newVehicles', newTableWeight === null ? '—' : this.formatDecimal(newTableWeight));
    this.setNewPeriodValue('Peso', 'usedVehicles', usedTableWeight === null ? '—' : this.formatDecimal(usedTableWeight));
    this.setNewPeriodValue(
      'Comissão/Contrato',
      'newVehicles',
      newCommissionPerContract === null ? '—' : this.formatCurrency(newCommissionPerContract)
    );
    this.setNewPeriodValue(
      'Comissão/Contrato',
      'usedVehicles',
      usedCommissionPerContract === null ? '—' : this.formatCurrency(usedCommissionPerContract)
    );
    this.setNewPeriodValue(
      'Comissão/Contrato',
      'total',
      newCommissionPerContract === null || usedCommissionPerContract === null
        ? '—'
        : this.formatCurrency((newCommissionPerContract + usedCommissionPerContract) / 2)
    );
    this.setNewPeriodValue(
      'Comissão/Mês',
      'newVehicles',
      newMonthlyCommission === null ? '—' : this.formatCurrency(newMonthlyCommission)
    );
    this.setNewPeriodValue(
      'Comissão/Mês',
      'usedVehicles',
      usedMonthlyCommission === null ? '—' : this.formatCurrency(usedMonthlyCommission)
    );
    this.setNewPeriodValue(
      'Comissão/Mês',
      'total',
      newMonthlyCommission === null || usedMonthlyCommission === null
        ? '—'
        : this.formatCurrency(newMonthlyCommission + usedMonthlyCommission)
    );

    const contractsRow = this.closingPeriodRows.find(row => row.label === 'Contratos/dia útil');
    if (contractsRow) {
      contractsRow.total = newContractsPerDay === null || usedContractsPerDay === null
        ? '—'
        : String(newContractsPerDay + usedContractsPerDay);
    }
  }

  private getMatrixCommissionIndex(): number | null {
    if (!this.hasClosingInput('Penetration', 'total') || !this.hasClosingInput('ISR', 'total')) {
      return null;
    }

    const penetration = this.getClosingValue('Penetration', 'total');
    const isr = this.getClosingValue('ISR', 'total');
    return this.getMatrixIndexFor(penetration, isr);
  }

  private hasClosingInput(
    label: string,
    vehicleType: 'newVehicles' | 'usedVehicles' | 'total'
  ): boolean {
    const row = this.closingPeriodRows.find(item => item.label === label);
    if (!row || !/\d/.test(row[vehicleType])) {
      return false;
    }

    return this.parseNumber(row[vehicleType]) !== 0
      || this.editedClosingInputs.has(`${label}:${vehicleType}`);
  }

  isMatrixCell(kind: 'realized' | 'simulated', rowIndex: number, columnIndex: number): boolean {
    const position = kind === 'realized'
      ? this.getRealizedMatrixPosition()
      : this.getSimulatedMatrixPosition();
    return position?.rowIndex === rowIndex && position.columnIndex === columnIndex;
  }

  private getRealizedMatrixPosition(): { rowIndex: number; columnIndex: number } | null {
    return this.getMatrixPosition(
      this.getRealizedValue('Penetration', 'total'),
      this.getRealizedValue('ISR', 'total')
    );
  }

  private getSimulatedMatrixPosition(): { rowIndex: number; columnIndex: number } | null {
    if (!this.hasClosingInput('Penetration', 'total') || !this.hasClosingInput('ISR', 'total')) {
      return null;
    }

    const penetrationRow = this.closingPeriodRows.find(row => row.label === 'Penetration');
    const isrRow = this.closingPeriodRows.find(row => row.label === 'ISR');
    if (!penetrationRow || !isrRow) {
      return null;
    }

    return this.getMatrixPosition(
      this.parseNumber(penetrationRow.total),
      this.parseNumber(isrRow.total)
    );
  }

  private getMatrixPosition(penetration: number, isr: number): { rowIndex: number; columnIndex: number } | null {
    const rowIndex = this.findPenetrationRangeIndex(penetration);
    const columnIndex = this.findRangeIndex(isr, this.penetrationColumns);
    return rowIndex < 0 || columnIndex < 0 ? null : { rowIndex, columnIndex };
  }

  private getMatrixIndexFor(penetration: number, isr: number): number {
    const rowIndex = this.findPenetrationRangeIndex(penetration);
    const columnIndex = this.findRangeIndex(isr, this.penetrationColumns);
    if (rowIndex < 0 || columnIndex < 0) {
      return 0;
    }

    return this.parseDecimal(this.penetrationRows[rowIndex].values[columnIndex]);
  }

  private findPenetrationRangeIndex(value: number): number {
    if (value >= 55) {
      return 0;
    }
    if (value <= 40) {
      return this.penetrationRows.length - 1;
    }

    return this.findRangeIndex(value, this.penetrationRows.map(row => row.label));
  }

  private findRangeIndex(value: number, ranges: string[]): number {
    return ranges.findIndex(range => {
      const normalized = range.replace(/,/g, '.').replace(/\s/g, '');
      const lowerBound = normalized.match(/^(>=|>)(-?\d+(?:\.\d+)?)$/);
      if (lowerBound) {
        const threshold = Number(lowerBound[2]);
        return lowerBound[1] === '>=' ? value >= threshold : value > threshold;
      }

      const upperBound = normalized.match(/^(<=|<)(-?\d+(?:\.\d+)?)$/);
      if (upperBound) {
        const threshold = Number(upperBound[2]);
        return upperBound[1] === '<=' ? value <= threshold : value < threshold;
      }

      const interval = normalized.match(/^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/);
      if (!interval) {
        return false;
      }

      const minimum = Number(interval[1]);
      const maximum = Number(interval[2]);
      return value >= minimum && value < Math.floor(maximum) + 1;
    });
  }

  private parseDecimal(value: string): number {
    const parsed = Number(value.trim().replace('%', '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getClosingValue(
    label: string,
    vehicleType: 'newVehicles' | 'usedVehicles' | 'total'
  ): number {
    const row = this.closingPeriodRows.find(item => item.label === label);
    return row ? this.parseNumber(row[vehicleType]) : 0;
  }

  private getNewPeriodValue(
    label: string,
    vehicleType: 'newVehicles' | 'usedVehicles' | 'total'
  ): number {
    const row = this.newPeriodRows.find(item => item.label === label);
    return row ? this.parseNumber(row[vehicleType]) : 0;
  }

  private getRealizedValue(
    label: string,
    vehicleType: 'newVehicles' | 'usedVehicles' | 'total'
  ): number {
    const row = this.realizedRows.find(item => item.label === label);
    return row ? this.parseNumber(row[vehicleType]) : 0;
  }

  private setNewPeriodValue(
    label: string,
    field: keyof NewPeriodRow,
    value: string
  ): void {
    const row = this.newPeriodRows.find(item => item.label === label);
    if (row) {
      row[field] = value;
    }
  }

  private parseNumber(value: string): number {
    const normalized = value
      .replace(/[^\d,.-]/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private formatDecimal(value: number): string {
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value);
  }

}
